class QuantityLimitHandler {
  constructor() {
    this.cartData = null;
    this.init();
  }

  init() {
    // Listen for variant changes to update limits
    subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
    
    // Listen for cart updates
    subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate.bind(this));
    
    // Listen for cart drawer open
    document.addEventListener('cart-drawer:open', () => {
      this.fetchCartData().then(() => {
        this.updateCartItemLimits();
      });
    });
    
    // Initialize on page load
    this.fetchCartData().then(() => {
      this.updateQuantityLimits();
      this.updateCartItemLimits();
    });
  }

  async fetchCartData() {
    try {
      const response = await fetch('/cart.js');
      this.cartData = await response.json();
    } catch (error) {
      console.error('Error fetching cart data:', error);
    }
  }

  handleCartUpdate() {
    // Refresh cart data and update limits
    this.fetchCartData().then(() => {
      this.updateQuantityLimits();
    });
  }

  getCartQuantity(variantId) {
    if (!this.cartData || !variantId) return 0;
    
    // Find all items with this variant ID, excluding free gifts
    const items = this.cartData.items.filter(item => {
      // Check if this is the variant we're looking for
      if (item.variant_id.toString() !== variantId.toString()) return false;
      
      // Exclude free gifts - check various conditions
      // 1. Check if price is 0 (common for gifts)
      if (item.price === 0) return false;
      
      // 2. Check for gift properties
      if (item.properties) {
        // Check for common gift property names
        for (const [key, value] of Object.entries(item.properties)) {
          const lowerKey = key.toLowerCase();
          if (lowerKey.includes('gift') || lowerKey.includes('free') || 
              lowerKey === '_is_gift' || lowerKey === 'is_gift' ||
              lowerKey === '_gift' || lowerKey === 'gift_with_purchase') {
            return false;
          }
        }
      }
      
      // 3. Check if line price is 0 (another indicator of a gift)
      if (item.line_price === 0 && item.original_line_price > 0) return false;
      
      return true;
    });
    
    // Sum up quantities of non-gift items
    return items.reduce((total, item) => total + item.quantity, 0);
  }

  handleVariantChange(event) {
    const { variant } = event.data;
    if (variant) {
      this.updateQuantityLimits(variant);
    }
  }

  updateQuantityLimits(variant = null) {
    const quantityCounters = document.querySelectorAll('quantity-counter:not([data-cart="true"])');
    quantityCounters.forEach(counter => {
      this.updateCounterLimit(counter, variant);
    });
  }

  updateCartItemLimits() {
    const cartQuantityCounters = document.querySelectorAll('quantity-counter[data-cart="true"]');
    cartQuantityCounters.forEach(counter => {
      this.updateCartCounterLimit(counter);
    });
  }

  updateCounterLimit(counter, variant) {
    const input = counter.querySelector('.js-counter-quantity');
    if (!input) return;

    // Get limit from data attribute or metafield
    const maxOrderLimit = this.getMaxOrderLimit(variant);
    
    if (maxOrderLimit) {
      // Get variant ID
      const form = counter.closest('form');
      let variantId = null;
      if (form) {
        const variantInput = form.querySelector('[name="id"]');
        if (variantInput) {
          variantId = variantInput.value;
        }
      }
      
      // Get existing cart quantity
      const cartQuantity = this.getCartQuantity(variantId);
      const availableLimit = maxOrderLimit - cartQuantity;
      
      // Set the effective limit (what's left available)
      const effectiveLimit = Math.max(0, availableLimit);
      input.setAttribute('max', effectiveLimit);
      counter.setAttribute('data-max-limit', maxOrderLimit);
      counter.setAttribute('data-effective-limit', effectiveLimit);
      counter.setAttribute('data-cart-quantity', cartQuantity);
      
      // Update the counter's max property
      if (counter.max !== effectiveLimit) {
        counter.max = effectiveLimit;
      }
      
      // Add click listener to increase button to show message when limit is reached
      const increaseBtn = counter.querySelector('.js-counter-increase');
      if (increaseBtn) {
        // Remove old listener
        if (increaseBtn.hasAttribute('data-limit-listener')) {
          const oldListener = increaseBtn._limitListener;
          if (oldListener) {
            increaseBtn.removeEventListener('click', oldListener, true);
          }
        }
        
        // Add new listener
        const limitListener = (e) => {
          const currentValue = parseInt(input.value);
          const cartQty = parseInt(counter.getAttribute('data-cart-quantity') || '0');
          const maxLimit = parseInt(counter.getAttribute('data-max-limit'));
          const totalQuantity = cartQty + currentValue;
          
          if (totalQuantity >= maxLimit) {
            e.preventDefault();
            e.stopPropagation();
            const formWrapper = counter.closest('.wt-product__add-to-cart_form--wrapper') || counter.closest('.wt-product__add-to-cart');
            if (formWrapper) {
              this.showLimitExceededMessage(formWrapper, maxLimit, cartQty);
            }
          }
        };
        
        increaseBtn._limitListener = limitListener;
        increaseBtn.setAttribute('data-limit-listener', 'true');
        increaseBtn.addEventListener('click', limitListener, true);
      }
      
      // Add input listener to handle manual typing
      if (!input.hasAttribute('data-limit-input-listener')) {
        input.setAttribute('data-limit-input-listener', 'true');
        input.addEventListener('input', (e) => {
          const value = parseInt(e.target.value);
          const cartQty = parseInt(counter.getAttribute('data-cart-quantity') || '0');
          const maxLimit = parseInt(counter.getAttribute('data-max-limit'));
          const totalQuantity = cartQty + value;
          
          if (totalQuantity > maxLimit) {
            e.target.value = maxLimit - cartQty;
            const formWrapper = counter.closest('.wt-product__add-to-cart_form--wrapper') || counter.closest('.wt-product__add-to-cart');
            if (formWrapper) {
              this.showLimitExceededMessage(formWrapper, maxLimit, cartQty);
            }
          }
        });
      }
      
      // Ensure current value doesn't exceed available limit
      if (parseInt(input.value) > effectiveLimit) {
        input.value = Math.max(1, effectiveLimit);
      }
    }
  }

  updateCartCounterLimit(counter) {
    const input = counter.querySelector('.js-counter-quantity');
    if (!input) return;

    const variantId = input.getAttribute('data-quantity-variant-id');
    
    // Check if this is a gift item by looking at the cart item container
    const cartItem = counter.closest('.wt-cart__item');
    if (cartItem) {
      // Skip limit for gift items - check data attributes or classes that might indicate a gift
      const price = cartItem.getAttribute('data-price');
      if (price === '0' || price === '0.00') {
        return; // Don't apply limits to free gifts
      }
      
      // Check for gift indicators in classes or data attributes
      if (cartItem.classList.contains('gift-item') || 
          cartItem.hasAttribute('data-gift-item') ||
          cartItem.hasAttribute('data-is-gift')) {
        return; // Don't apply limits to gifts
      }
    }
    
    const maxOrderLimit = this.getCartItemMaxLimit(variantId);
    
    if (maxOrderLimit) {
      input.setAttribute('max', maxOrderLimit);
      counter.setAttribute('data-max-limit', maxOrderLimit);
      
      // Update the counter's max property
      if (counter.max !== maxOrderLimit) {
        counter.max = maxOrderLimit;
      }
      
      // Add click listener to increase button to show message when limit is reached
      const increaseBtn = counter.querySelector('.js-counter-increase');
      if (increaseBtn && !increaseBtn.hasAttribute('data-limit-listener')) {
        increaseBtn.setAttribute('data-limit-listener', 'true');
        increaseBtn.addEventListener('click', (e) => {
          // Check BEFORE the value is increased
          const currentValue = parseInt(input.value);
          if (currentValue >= maxOrderLimit) {
            e.preventDefault();
            e.stopPropagation();
            const itemBody = counter.closest('.wt-cart__item__body');
            if (itemBody) {
              this.showCartLimitExceededMessage(itemBody, maxOrderLimit);
            }
          }
        }, true); // Use capture phase to intercept before the increase happens
      }
      
      // Add input listener to handle manual typing in cart
      if (!input.hasAttribute('data-limit-input-listener')) {
        input.setAttribute('data-limit-input-listener', 'true');
        input.addEventListener('input', (e) => {
          const value = parseInt(e.target.value);
          if (value > maxOrderLimit) {
            e.target.value = maxOrderLimit;
            const itemBody = counter.closest('.wt-cart__item__body');
            if (itemBody) {
              this.showCartLimitExceededMessage(itemBody, maxOrderLimit);
            }
          }
        });
      }
    }
  }

  getMaxOrderLimit(variant) {
    // Try to get from variant metafield if variant is provided
    if (variant && variant.metafields && variant.metafields.custom && variant.metafields.custom.max_order_limit) {
      return parseInt(variant.metafields.custom.max_order_limit);
    }
    
    // Try to get from product data attribute
    const productForm = document.querySelector('product-form');
    if (productForm) {
      const limitAttr = productForm.getAttribute('data-max-order-limit');
      if (limitAttr) {
        return parseInt(limitAttr);
      }
    }
    
    return null;
  }

  getCartItemMaxLimit(variantId) {
    // Try to get from cart item data
    const cartItem = document.querySelector(`[data-quantity-variant-id="${variantId}"]`);
    if (cartItem) {
      const itemContainer = cartItem.closest('.wt-cart__item');
      if (itemContainer) {
        const limitAttr = itemContainer.getAttribute('data-max-order-limit');
        if (limitAttr) {
          return parseInt(limitAttr);
        }
      }
    }
    
    return null;
  }

  showLimitMessage(counter, limit) {
    // Don't show message immediately - only when limit is exceeded
    return;
  }
  
  showLimitExceededMessage(formWrapper, limit, cartQuantity = 0) {
    // Remove existing message
    const existingMessage = formWrapper.querySelector('.quantity-limit-message');
    if (existingMessage) {
      existingMessage.remove();
    }
    
    // Create and insert message
    const message = document.createElement('div');
    message.className = 'quantity-limit-message quantity-limit-message--error';
    
    // Always use the localized base message
    let messageText = window.quantityLimitStrings?.limitMessage?.replace('[limit]', limit) || `Maximum ${limit} per order`;
    
    // Add cart quantity info if applicable
    if (cartQuantity > 0) {
      if (cartQuantity >= limit) {
        const alreadyInCartText = window.quantityLimitStrings?.alreadyInCart?.replace('[quantity]', cartQuantity) || `You already have ${cartQuantity} in your cart`;
        messageText += `. ${alreadyInCartText}`;
      } else {
        const remaining = limit - cartQuantity;
        const canAddMoreText = window.quantityLimitStrings?.canAddMore?.replace('[existing]', cartQuantity).replace('[remaining]', remaining) || `You already have ${cartQuantity} in cart, can add ${remaining} more`;
        messageText += `. ${canAddMoreText}`;
      }
    }
    
    message.textContent = messageText;
    
    // Find the best place to insert the message
    if (formWrapper.classList.contains('wt-product__add-to-cart_form--wrapper')) {
      // For product page, insert at the top of form wrapper
      formWrapper.insertBefore(message, formWrapper.firstChild);
    } else {
      // For other cases, insert before the form
      const form = formWrapper.querySelector('form');
      if (form && form.parentElement) {
        form.parentElement.insertBefore(message, form);
      } else {
        formWrapper.insertBefore(message, formWrapper.firstChild);
      }
    }
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      message.classList.add('fade-out');
      setTimeout(() => message.remove(), 300);
    }, 5000);
  }

  showCartLimitMessage(counter, limit) {
    // Don't show message immediately - only when limit is exceeded
    return;
  }
  
  showCartLimitExceededMessage(itemContainer, limit) {
    // Remove existing message
    const existingMessage = itemContainer.querySelector('.quantity-limit-message');
    if (existingMessage) {
      existingMessage.remove();
    }
    
    // Create and insert message
    const message = document.createElement('div');
    message.className = 'quantity-limit-message quantity-limit-message--cart quantity-limit-message--error';
    message.textContent = window.quantityLimitStrings?.limitMessage?.replace('[limit]', limit) || `Maximum ${limit} per order`;
    
    // Insert message in the data area at the top
    const dataArea = itemContainer.querySelector('.wt-cart__item__data');
    if (dataArea) {
      dataArea.insertBefore(message, dataArea.firstChild);
    }
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      message.classList.add('fade-out');
      setTimeout(() => message.remove(), 300);
    }, 5000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new QuantityLimitHandler();
  });
} else {
  new QuantityLimitHandler();
} 